<script setup lang="ts">
/**
 * Główny komponent aplikacji.
 * Zawiera layout z NavBar, router-view i Footer.
 * Widok gry (PlayView) renderuje się bez layoutu.
 */
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ACCOUNT_RESTRICTED_EVENT, SESSION_EXPIRED_EVENT } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { useLobbyStore, type PresenceState } from '@/stores/lobby'
import NavBar from '@/components/layout/NavBar.vue'
import Footer from '@/components/layout/Footer.vue'
import NotificationToast from '@/components/common/NotificationToast.vue'
import UploadProgressStack from '@/components/common/UploadProgressStack.vue'

let popupObserver: MutationObserver | null = null
let popupFrame = 0
let popupOpenSequence = 0
const popupOrder = new Map<HTMLElement, number>()

function coordinatePopupSurfaces(): void {
  cancelAnimationFrame(popupFrame)
  popupFrame = requestAnimationFrame(() => {
    const selector = [
      '.modal-overlay.active',
      '.profile-overlay',
      '.friends-popup-overlay.active',
      '.auth-view-shell.embedded',
      '.report-overlay',
      '.player-profile-overlay',
      '.information-overlay',
      '.options-overlay',
      '.crop-overlay',
      '.emote-calibration-overlay',
      '.admin-user-overlay',
      '.instruction-overlay',
      '.audit-details',
    ].join(', ')
    const overlays = Array.from(document.querySelectorAll<HTMLElement>(selector))
      .filter(element => element.isConnected)
    const openSet = new Set(overlays)
    for (const [overlay] of popupOrder) {
      if (openSet.has(overlay)) continue
      overlay.classList.remove('nhex-popup-suspended')
      overlay.inert = false
      overlay.removeAttribute('aria-hidden')
      popupOrder.delete(overlay)
    }
    for (const overlay of overlays) {
      if (!popupOrder.has(overlay)) popupOrder.set(overlay, ++popupOpenSequence)
    }
    const active = overlays.reduce<HTMLElement | undefined>((latest, overlay) => (
      !latest || (popupOrder.get(overlay) ?? 0) > (popupOrder.get(latest) ?? 0) ? overlay : latest
    ), undefined)
    for (const overlay of overlays) {
      const suspended = overlay !== active
      overlay.classList.toggle('nhex-popup-suspended', suspended)
      overlay.inert = suspended
      if (suspended) overlay.setAttribute('aria-hidden', 'true')
      else overlay.removeAttribute('aria-hidden')
    }
  })
}

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const lobby = useLobbyStore()

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return Boolean(target.closest('input, textarea, [contenteditable="true"], [data-native-context]'))
}

function preventUnusedContextMenu(event: MouseEvent): void {
  if (!isEditableTarget(event.target)) event.preventDefault()
}

function preventUnusedNativeDrag(event: DragEvent): void {
  const target = event.target
  if (!(target instanceof Element)) {
    event.preventDefault()
    return
  }
  if (target.closest('[draggable="true"], input[type="file"], [data-native-drag]')) return
  event.preventDefault()
}

function handleSessionExpired(): void {
  const redirect = route.meta.requiresAuth ? route.fullPath : ''
  auth.invalidateSession()
	void router.replace({
		name: 'lobby',
		query: redirect
			? { auth: 'login', redirect, reason: 'session-expired' }
			: { auth: 'login', reason: 'session-expired' },
	})
}

function handleAccountRestricted(): void {
	auth.invalidateSession()
	void router.replace({ name: 'account-restricted' })
}

function presenceForRoute(): PresenceState {
  if (route.name === 'lobby') return { activity: 'available', tableId: null }
  if (route.name === 'play') {
    const routeId = route.params.id
    return {
      activity: 'table',
      tableId: typeof routeId === 'string' ? routeId : null,
    }
  }
  if (route.path === '/armies' || route.path.startsWith('/armies/')) {
    return { activity: 'armies', tableId: null }
  }
	if (route.path === '/admin' || route.path.startsWith('/admin/')) {
		return { activity: 'admin', tableId: null }
	}
	if (['tutorial', 'terms', 'support'].includes(String(route.name || ''))) {
		return { activity: 'footer', tableId: null }
	}
  return { activity: 'unavailable', tableId: null }
}

const stopPresenceWatch = watch(
  () => [auth.isAuthenticated, route.name, route.params.id, route.path] as const,
  () => {
    lobby.startHeartbeat(presenceForRoute())
  },
  { immediate: true },
)

onMounted(() => {
	lobby.startChatRealtime()
  window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
	window.addEventListener(ACCOUNT_RESTRICTED_EVENT, handleAccountRestricted)
  document.addEventListener('contextmenu', preventUnusedContextMenu)
  document.addEventListener('dragstart', preventUnusedNativeDrag)
  popupObserver = new MutationObserver(coordinatePopupSurfaces)
  popupObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style'] })
  coordinatePopupSurfaces()
})
onBeforeUnmount(() => {
  stopPresenceWatch()
  lobby.stopHeartbeat()
	lobby.stopChatRealtime()
  window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired)
	window.removeEventListener(ACCOUNT_RESTRICTED_EVENT, handleAccountRestricted)
  document.removeEventListener('contextmenu', preventUnusedContextMenu)
  document.removeEventListener('dragstart', preventUnusedNativeDrag)
  popupObserver?.disconnect()
  popupObserver = null
  popupOrder.clear()
  cancelAnimationFrame(popupFrame)
})

/* Gra wyświetla się pełnoekranowo bez nawigacji */
const isPlayView = computed(() => route.name === 'play')
const hideLayoutOnAuth = computed(() => {
  const authViews = new Set([
    'password-reset',
    'password-reset-confirm',
    'nickname-change',
    'forgot-username',
    'activate',
  ])
  return authViews.has(String(route.name ?? ''))
})
</script>

<template>
  <!-- SVG Filter Definition for smoother text-stroke rendering -->
  <svg class="svg-filter" aria-hidden="true" style="position: absolute; width: 0; height: 0; pointer-events: none;">
    <defs>
      <filter id="goo">
        <feGaussianBlur in="SourceGraphic" stdDeviation="1.5" result="blur" />
        <feColorMatrix in="blur" mode="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9" result="goo" />
        <feComposite in="SourceGraphic" in2="goo" operator="atop"/>
      </filter>
    </defs>
  </svg>

  <!-- Full-screen game view — no nav/footer -->
  <div v-if="isPlayView" class="h-screen w-screen overflow-hidden">
    <RouterView />
  </div>

  <!-- Standard layout -->
  <div v-else class="min-h-screen flex flex-col"
    :style="{
      backgroundImage: `url('/media/site/new_york.avif')`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed'
    }">
    <NavBar v-if="!hideLayoutOnAuth" />
    <main
      :class="hideLayoutOnAuth
        ? 'flex-1 flex items-center justify-center px-4 py-6'
        : 'flex-1 container mx-auto px-4 py-6'"
    >
      <RouterView />
    </main>
    <Footer />
  </div>

  <!-- Global toast notifications -->
  <NotificationToast />
  <UploadProgressStack />
</template>
